/** Generic schema-driven widget table.
 *
 *  Every archetype widget (Labor Dispute Register · Craft Rate Card ·
 *  Royalty Distribution Register · …) uses THIS ONE component,
 *  parameterised by the widget's column schema fetched from
 *  /api/trade-unions/{slug}/dashboard/config.
 *
 *  Props
 *  ─────
 *    slug         - union slug
 *    widget_type  - one of the 14 widget slugs
 *    schema       - { label, blurb, columns: [{key, label, type, options?}] }
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Info } from "lucide-react";

export default function WidgetTable({ slug, widget_type, schema }) {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft]   = useState({});
  const [saving, setSaving] = useState(false);

  const emptyDraft = useCallback(() => {
    const d = {};
    for (const c of schema.columns) {
      d[c.key] = c.type === "int" ? 0 : "";
    }
    return d;
  }, [schema]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/trade-unions/${slug}/widget/${widget_type}`);
      setRows(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load widget rows.");
    } finally { setLoading(false); }
  }, [slug, widget_type]);

  useEffect(() => {
    setDraft(emptyDraft());
    refresh();
  }, [emptyDraft, refresh]);

  const addRow = async () => {
    setSaving(true);
    try {
      await api.post(`/trade-unions/${slug}/widget/${widget_type}`, draft);
      setDraft(emptyDraft());
      toast.success("Row added.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add row.");
    } finally { setSaving(false); }
  };

  const deleteRow = async (row_id) => {
    if (!window.confirm("Delete this row?")) return;
    try {
      await api.delete(`/trade-unions/${slug}/widget/${widget_type}/${row_id}`);
      toast.success("Row removed.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove row.");
    }
  };

  return (
    <div className="cs-card rounded-2xl p-6" data-testid={`widget-${widget_type}`}>
      <div className="mb-4">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">
          {schema.label}
        </div>
        <p className="mt-1 text-[12px] text-white/55">{schema.blurb}</p>
      </div>

      {/* Add-row form */}
      <div className="rounded-xl border border-white/10 p-4 bg-white/[0.02]">
        <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 mb-2">
          New Row
        </div>
        <div className="grid md:grid-cols-3 gap-2">
          {schema.columns.map((c) => (
            <FieldInput key={c.key} column={c}
                        value={draft[c.key]}
                        onChange={(v) => setDraft((d) => ({ ...d, [c.key]: v }))}
                        testid={`widget-${widget_type}-input-${c.key}`}/>
          ))}
        </div>
        <div className="mt-3">
          <Button onClick={addRow} disabled={saving}
                  className="btn-themed h-9 px-4 rounded-full text-[11px] uppercase tracking-[0.22em]"
                  data-testid={`widget-${widget_type}-add`}>
            <Plus size={13}/> {saving ? "Adding…" : "Add Row"}
          </Button>
        </div>
      </div>

      {/* Rows */}
      <div className="mt-5">
        {loading ? (
          <div className="text-[11px] text-white/40">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-[12px] text-white/45 flex items-center gap-2">
            <Info size={12}/> No entries yet — add the first row above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid={`widget-${widget_type}-table`}>
              <thead>
                <tr className="text-left text-[9px] uppercase tracking-[0.22em] text-white/40 border-b border-white/10">
                  {schema.columns.map((c) => (
                    <th key={c.key} className="py-2 pr-3">{c.label}</th>
                  ))}
                  <th className="py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/[0.06] hover:bg-white/[0.03] transition">
                    {schema.columns.map((c) => (
                      <td key={c.key} className="py-2 pr-3 text-white/80 max-w-[220px] truncate">
                        {formatCell(r.data?.[c.key], c)}
                      </td>
                    ))}
                    <td className="py-2 text-right">
                      <button onClick={() => deleteRow(r.id)}
                              className="text-white/40 hover:text-red-400 transition"
                              data-testid={`widget-${widget_type}-delete-${r.id}`}>
                        <Trash2 size={12}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function FieldInput({ column, value, onChange, testid }) {
  const c = column;
  if (c.type === "longtext") {
    return (
      <Textarea value={value || ""} rows={2}
                onChange={(e) => onChange(e.target.value)}
                placeholder={c.label}
                className="md:col-span-3"
                data-testid={testid}/>
    );
  }
  if (c.type === "enum") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}>
          <SelectValue placeholder={c.label} />
        </SelectTrigger>
        <SelectContent>
          {(c.options || []).map((opt) => (
            <SelectItem key={opt} value={opt}>{humanEnum(opt)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (c.type === "date") {
    return (
      <Input type="date" value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder={c.label} data-testid={testid}/>
    );
  }
  if (c.type === "int") {
    return (
      <Input type="number" value={value || 0}
              onChange={(e) => onChange(Number(e.target.value))}
              placeholder={c.label} data-testid={testid}/>
    );
  }
  return (
    <Input value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={c.label} data-testid={testid}/>
  );
}


function formatCell(v, col) {
  if (v === "" || v === null || v === undefined) return "—";
  if (col.type === "int") return Number(v).toLocaleString("en-IN");
  if (col.type === "enum") return humanEnum(v);
  return String(v);
}


function humanEnum(k) {
  return String(k || "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}
